(() => {
  const slugify = (text) =>
    (text || '')
      .toLowerCase()
      .trim()
      .replace(/[^\p{L}\p{N}]+/gu, '-')
      .replace(/^-+|-+$/g, '');

  function init(wrapper) {
    // Idempotent — building the nav moves/clones DOM, so never run twice on the
    // same wrapper. A freshly editor-loaded section is new DOM without the flag.
    if (wrapper.dataset.contentSectionsReady === 'true') return;

    const sidebarNav = wrapper.querySelector('[data-content-nav]');
    const main = wrapper.querySelector('[data-content-main]');
    if (!main) return;

    wrapper.dataset.contentSectionsReady = 'true';

    // Resolve the mobile drawer for THIS section. The chapters bar + drawer are
    // siblings of the content column (not descendants of the wrapper), so scope
    // to the section root. Then pin the exact drawer via the toggle's
    // aria-controls id so multiple instances on a page never cross-wire.
    // The drawer is an <info-drawer> shell (content-sections-chapters.liquid):
    // opening, closing, Escape, backdrop, focus trap and body scroll-lock are
    // all the shell's — this file only fills [data-drawer-nav] and closes the
    // drawer when a chapter is picked.
    const sectionRoot = wrapper.closest('.shopify-section') || document;
    const chaptersBtn = sectionRoot.querySelector('[data-chapters-toggle]');
    let drawer = null;
    if (chaptersBtn) {
      const controls = chaptersBtn.getAttribute('aria-controls');
      if (controls) drawer = document.getElementById(controls);
    }
    if (!drawer) drawer = sectionRoot.querySelector('[data-chapters-drawer]');
    const drawerNav = drawer ? drawer.querySelector('[data-drawer-nav]') : null;

    const navLinkClass = 'content-sections__nav-link';
    const navActiveClass = navLinkClass + '--active';

    let navItems = Array.from(main.querySelectorAll('[data-content-nav-item]'));

    // Metaobject-driven body (e.g. Store policies): no authored nav anchors.
    // Synthesise the nav from the rich-text h2 headings — slugify each into a
    // unique id so the sidebar/drawer links + scroll-spy (which keys off
    // h2[id]) all work without any Liquid-side markup.
    if (navItems.length === 0 && wrapper.hasAttribute('data-content-autonav')) {
      const usedIds = new Set();
      navItems = Array.from(main.querySelectorAll('h2'))
        // Word-pasted policy copy carries empty headings as spacers — <h2></h2>,
        // <h2>&nbsp;</h2>, <h2><br></h2>. There is no label to put in the nav, so
        // drop them here. Skipping also means they get no id, which keeps them out
        // of the scroll-spy's h2[id] list below. (trim() covers &nbsp;: U+00A0 is
        // WhiteSpace per spec.)
        .filter((heading) => heading.textContent.trim() !== '')
        .map((heading) => {
          const base = heading.id || slugify(heading.textContent) || 'section';
          let id = base;
          let n = 2;
          while (usedIds.has(id)) {
            id = base + '-' + n;
            n += 1;
          }
          usedIds.add(id);
          heading.id = id;

          const link = document.createElement('a');
          link.className = 'content-section-nav-link';
          link.setAttribute('href', '#' + id);
          link.setAttribute('data-content-nav-item', '');
          link.style.display = 'none';
          link.textContent = heading.textContent.trim();
          return link;
        });
    }

    navItems.forEach((item) => {
      item.classList.add(navLinkClass);
      item.style.display = '';
      item.dataset.text = item.textContent.trim();

      if (drawerNav) {
        const clone = item.cloneNode(true);
        clone.dataset.drawerLink = 'true';
        drawerNav.appendChild(clone);
      }

      if (sidebarNav) {
        const wrapper = document.createElement('div');
        wrapper.className = 'content-sections__nav-item';
        wrapper.appendChild(item);
        sidebarNav.appendChild(wrapper);
      }
    });

    if (!sidebarNav && navItems.length === 0) return;

    const allLinks = [
      ...(sidebarNav ? Array.from(sidebarNav.querySelectorAll('.' + navLinkClass)) : []),
      ...(drawerNav ? Array.from(drawerNav.querySelectorAll('.' + navLinkClass)) : [])
    ];

    const headings = Array.from(main.querySelectorAll('h2[id]'));
    if (!headings.length) return;

    function getHeaderOffset() {
      const sticky = document.querySelector('sticky-header.shopify-section-header-sticky')
        || document.querySelector('sticky-header');
      const headerHeight = sticky ? sticky.getBoundingClientRect().height : 0;
      // AAS-5309: +40 (not +24) — the header's frost band blurs ~40px below
      // the pill (.header__bg-layer masks), so scrolled-to headings landed
      // with their top line inside the blur. Matches the sticky sidebar's
      // top offset in content-sections.css.
      return (headerHeight || 0) + 40;
    }

    function setActive(id) {
      allLinks.forEach((l) => {
        const matches = l.getAttribute('href') === `#${id}`;
        l.classList.toggle(navActiveClass, matches);
      });
    }

    // Picking a chapter closes the drawer through the shell so focus returns
    // to the chapters button and the body scroll-lock lifts before the smooth
    // scroll below runs. (Opening needs no JS here — the button carries
    // [data-drawer-open], handled globally by info-drawer.js.)
    function closeDrawer() {
      if (drawer && typeof drawer.close === 'function') drawer.close();
    }

    let isProgrammaticScroll = false;
    let programmaticTimer = null;

    allLinks.forEach((link) => {
      link.addEventListener('click', (e) => {
        const href = link.getAttribute('href');
        if (!href || !href.startsWith('#')) return;
        const id = href.slice(1);
        const target = document.getElementById(id);
        if (!target) return;
        e.preventDefault();

        setActive(id);

        if (link.dataset.drawerLink === 'true') {
          closeDrawer();
        }

        isProgrammaticScroll = true;
        clearTimeout(programmaticTimer);

        const offset = getHeaderOffset();
        const top = target.getBoundingClientRect().top + window.scrollY - offset;
        const maxTop = document.documentElement.scrollHeight - window.innerHeight;
        const finalTop = Math.max(0, Math.min(top, maxTop));

        window.scrollTo({ top: finalTop, behavior: 'smooth' });

        programmaticTimer = setTimeout(() => {
          isProgrammaticScroll = false;
          updateActiveFromScroll();
        }, 900);
      });
    });

    function updateActiveFromScroll() {
      if (isProgrammaticScroll) return;
      const threshold = getHeaderOffset() + 40;
      let activeId = headings[0].id;

      for (const h of headings) {
        const top = h.getBoundingClientRect().top;
        if (top - threshold <= 0) {
          activeId = h.id;
        } else {
          break;
        }
      }

      const scrolledToBottom =
        window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 2;
      if (scrolledToBottom) {
        activeId = headings[headings.length - 1].id;
      }

      setActive(activeId);
    }

    let rafId = null;
    window.addEventListener(
      'scroll',
      () => {
        if (rafId) return;
        rafId = requestAnimationFrame(() => {
          updateActiveFromScroll();
          rafId = null;
        });
      },
      { passive: true }
    );

    updateActiveFromScroll();
  }

  function initAll(root) {
    (root || document).querySelectorAll('[data-content-sections]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  // Theme editor: sections are injected/replaced via AJAX after this script has
  // already run, so (re)build the nav for any section that loads or re-renders.
  document.addEventListener('shopify:section:load', (event) => {
    initAll(event.target);
  });
})();
